import React, { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import DropDown from '../../default/form/DropDown'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import allRoute from '../route/route'
import { useChecker } from '../../../utils/admin/AdminRouteHook'
import { useRequestFromSearch, useChangeUrlSubmit, useGetData } from '../../../utils/default/ComplexFlowHook'
import { searchToRequest, RequestType } from '../../../views/admin/finance/FinanceDepositWayGroupPage'
import { isUndefined, omitBy } from '@golden/utils'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { FinanceDepositWayGroupQuery, FinanceDepositFeeMethodQuery, FinanceDepositQueryList } from '@golden/gdk-admin'
import { forkJoin } from 'rxjs'

const FinanceDepositWayGroupToolbar: React.FC = () => {
  const request = useRequestFromSearch({ searchToRequest })
  const classes = useCommonStyles()
  const { t } = useT()
  const gdk = useGDK()

  const [depositQueryData, setDepositQueryData] = useState<FinanceDepositQueryList | null>(null)
  const [methodList, setMethodList] = useState<FinanceDepositFeeMethodQuery[]>([])

  const methodOptions = useMemo(() => [
    { name: t('common.allMethod'), value: '--' },
    ...depositQueryData?.methods.filter(data => methodList.some(item => item.method === data.method)).map(({ name, method, group_count: groupCount }) => ({ name: `${name}(${groupCount})`, value: method })
    ) ?? []
  ], [methodList, depositQueryData, t])

  const statusOptions = useMemo(() => ([
    { name: t('common.allStatus'), value: 2 },
    { name: t('common.toggleOn'), value: 1 },
    { name: t('common.toggleOff'), value: 0 }
  ]), [t])

  useGetData({
    gdkFunc: () => forkJoin([gdk.finance.getDepositQueryList({
      is_active: request?.is_active === 2 ? undefined : request?.is_active,
      method: request?.method === '--' ? undefined : request?.method
    } as FinanceDepositWayGroupQuery), gdk.finance.getDepositFeeMethod()]),
    gdkFuncDependencies: [gdk],
    onSuccess: ([query, methods]: [FinanceDepositQueryList, FinanceDepositFeeMethodQuery[]]) => {
      setDepositQueryData(query)
      setMethodList(methods)
    }
  })

  const handleStatusSubmit = useChangeUrlSubmit<{ is_active: number }, RequestType>({
    formToRequest: (form) => {
      const converted = {
        is_active: form.is_active,
        method: request?.method,
        page: 1
      } as RequestType
      return omitBy(converted, isUndefined) as RequestType
    },
    encodePath: allRoute.financeDepositWayGroup.encodePath,
    toAddNowTimestamp: true
  })

  const handleMethodSubmit = useChangeUrlSubmit<{ method: string }, RequestType>({
    formToRequest: (form) => {
      const converted = {
        is_active: request?.is_active,
        method: form.method === '--' ? undefined : form.method,
        page: 1
      } as RequestType
      return omitBy(converted, isUndefined) as RequestType
    },
    encodePath: allRoute.financeDepositWayGroup.encodePath,
    toAddNowTimestamp: true
  })

  const updateQueryListCount = (payload: { is_active: number | undefined }) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { is_active } = payload
    const converted = {
      is_active: is_active === 2 ? undefined : is_active
    } as FinanceDepositWayGroupQuery
    gdk.finance.getDepositQueryList(omitBy(converted, isUndefined)).subscribe(res => { setDepositQueryData(res) })
  }

  const writable = useChecker()

  return (
    <Grid container direction="row" justifyContent="flex-end" spacing={2}>
      <Grid item xs={6} md={2}>
        <DropDown
          className={classes.outlineDropDown}
          fullWidth
          variant="outlined"
          label=""
          value={request?.is_active ?? 2}
          onChange={useCallback((event) => {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const is_active = event.target.value as number
            handleStatusSubmit({ is_active })
            updateQueryListCount({ is_active })
          }, [handleStatusSubmit, updateQueryListCount])}
          options={statusOptions}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <DropDown
          className={classes.outlineDropDown}
          fullWidth
          variant="outlined"
          label=""
          value={request?.method ?? '--'}
          onChange={useCallback((event) => {
            const method = event.target.value as string
            handleMethodSubmit({ method })
          }, [handleMethodSubmit])}
          options={methodOptions}
        />
      </Grid>
      {writable && (
        <Grid item>
          <Button
            component={Link}
            to={allRoute.financeDepositWayGroupCreate.encodePath({ search: { ...request }, param: {} })}
            className={classes.purpleGradualButton}
            classes={{ disabled: classes.disabledButton }}
          >
            <Box paddingY={0.5}>
              {t('common.createDepositWayGroup')}
            </Box>
          </Button>
        </Grid>
      )}
    </Grid>
  )
}

export default React.memo(FinanceDepositWayGroupToolbar)
